import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../config/api';
import {
  isOfflineStorageAvailable,
  saveTripSnapshot,
  getTripSnapshot,
  deleteTripSnapshot
} from '../utils/offlineTripStore';

/**
 * Keeps one trip available without a network.
 *
 * Saving is deliberately explicit rather than automatic. A traveller on airport
 * roaming should decide what gets downloaded, and an automatic cache of every
 * trip they open would also mean silently storing itineraries they only glanced
 * at on a device they may not own.
 *
 * The three requests below are what the itinerary needs to render: the trip
 * itself, the shared day-by-day items, and the traveller's personal items.
 * Anything else on the page (weather, suggestions, chat) is either live data or
 * not part of the offline promise.
 */
const useOfflineTrip = (tripId, userId) => {
  const [snapshot, setSnapshot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState(false);
  const isMountedRef = useRef(true);

  const supported = isOfflineStorageAvailable();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!supported || !tripId || !userId) {
      setSnapshot(null);
      setChecked(true);
      return;
    }

    let cancelled = false;

    getTripSnapshot(userId, tripId)
      .then((existing) => {
        if (!cancelled && isMountedRef.current) {
          setSnapshot(existing);
        }
      })
      .catch(() => {
        // A snapshot we cannot read is the same as not having one; the page
        // still works online, so this is not worth interrupting anyone over.
        if (!cancelled && isMountedRef.current) setSnapshot(null);
      })
      .finally(() => {
        if (!cancelled && isMountedRef.current) setChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [supported, tripId, userId]);

  const save = useCallback(async () => {
    if (!supported || !tripId || !userId) {
      setError('unsupported');
      return null;
    }

    setSaving(true);
    setError(null);

    try {
      const [tripResponse, itineraryResponse, memberItineraryResponse] = await Promise.all([
        api.get(`/trips/${tripId}`),
        api.get(`/trips/${tripId}/itinerary`),
        api.get(`/member-itinerary/trip/${tripId}`)
      ]);

      const saved = await saveTripSnapshot({
        userId,
        tripId,
        trip: tripResponse.data,
        itinerary: itineraryResponse.data || [],
        memberItinerary:
          memberItineraryResponse.data?.items || memberItineraryResponse.data || []
      });

      if (isMountedRef.current) setSnapshot(saved);
      return saved;
    } catch (err) {
      if (isMountedRef.current) {
        // Saving needs a network by definition, so being offline is the most
        // likely failure and deserves its own message.
        setError(err.response ? 'failed' : 'offline');
      }
      return null;
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [supported, tripId, userId]);

  const remove = useCallback(async () => {
    if (!supported || !tripId || !userId) return;

    try {
      await deleteTripSnapshot(userId, tripId);
      if (isMountedRef.current) {
        setSnapshot(null);
        setError(null);
      }
    } catch {
      if (isMountedRef.current) setError('failed');
    }
  }, [supported, tripId, userId]);

  return {
    supported,
    snapshot,
    isSaved: Boolean(snapshot),
    savedAt: snapshot?.savedAt || null,
    saving,
    error,
    checked,
    save,
    remove
  };
};

export default useOfflineTrip;
